import React, { useState, useEffect } from 'react';
import { apiService, type SubmitResponse } from '../services/apiService';
import { STORAGE_KEYS, SAMPLE_DATA_ARRAY } from '../utils/constants';
import './SimpleApp.css';

const SimpleApp: React.FC = () => {
  const [functionKey, setFunctionKey] = useState('');
  const [dataArray, setDataArray] = useState('');
  const [submissionId, setSubmissionId] = useState('');
  const [scores, setScores] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load saved function key on mount
  useEffect(() => {
    const savedKey = localStorage.getItem(STORAGE_KEYS.API_KEY);
    if (savedKey) {
      setFunctionKey(savedKey);
      apiService.setFunctionKey(savedKey);
    }
  }, []);

  const handleFunctionKeyChange = (key: string) => {
    setFunctionKey(key);
    apiService.setFunctionKey(key);
    localStorage.setItem(STORAGE_KEYS.API_KEY, key);
  };

  const loadSampleData = () => {
    setDataArray(JSON.stringify(SAMPLE_DATA_ARRAY, null, 2));
  };

  const handleSubmit = async () => {
    if (!functionKey.trim()) {
      setError('Please enter a function key');
      return;
    }

    if (!dataArray.trim()) {
      setError('Please enter data array');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const parsedData = JSON.parse(dataArray);
      const response: SubmitResponse = await apiService.submitData(parsedData);
      setSubmissionId(response.submissionid);
      setError('');
    } catch (err) {
      console.error('Submit error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit data');
    } finally {
      setLoading(false);
    }
  };

  const handleGetScores = async (all: boolean = false) => {
    if (!submissionId.trim()) {
      setError('No submission ID available');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await apiService.getScores(submissionId, all);
      setScores(result);
      setError('');
    } catch (err) {
      console.error('Get scores error:', err);
      setError(err instanceof Error ? err.message : 'Failed to get scores');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDataArray('');
    setSubmissionId('');
    setScores(null);
    setError('');
  };

  return (
    <div className="simple-app">
      <header className="app-header">
        <h1>{process.env.REACT_APP_APP_NAME || 'XTND Scoring API Test'}</h1>
        <p>
          Simple interface for testing XTND scoring endpoints
          {process.env.REACT_APP_ENVIRONMENT && (
            <span className={`environment-badge ${process.env.REACT_APP_ENVIRONMENT === 'staging' ? 'staging' : ''}`}>
              {process.env.REACT_APP_ENVIRONMENT.toUpperCase()}
            </span>
          )}
        </p>
      </header>

      <div className="app-content">
        {/* Function Key Input */}
        <div className="section">
          <h2>1. Azure Function Key</h2>
          <div className="input-group">
            <input
              type="password"
              placeholder="Enter Azure Function key"
              value={functionKey}
              onChange={(e) => handleFunctionKeyChange(e.target.value)}
              className="function-key-input"
            />
            <button
              type="button"
              onClick={() => {
                const input = document.querySelector('.function-key-input') as HTMLInputElement;
                input.type = input.type === 'password' ? 'text' : 'password';
              }}
              className="toggle-key-btn"
            >
              👁️
            </button>
          </div>
        </div>

        {/* Data Input */}
        <div className="section">
          <h2>2. Data Array (JSON)</h2>
          <div className="data-controls">
            <button onClick={loadSampleData} className="sample-btn">
              Load Sample Data
            </button>
            <button onClick={resetForm} className="reset-btn">
              Reset Form
            </button>
          </div>
          <textarea
            placeholder="Enter data array as JSON (e.g., ['value1', 'value2', null, ...])"
            value={dataArray}
            onChange={(e) => setDataArray(e.target.value)}
            className="data-textarea"
            rows={10}
          />
        </div>

        {/* Submit Button */}
        <div className="section">
          <button
            onClick={handleSubmit}
            disabled={loading || !functionKey || !dataArray}
            className="submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit for Scoring'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-section">
            <h3>Error</h3>
            <pre className="error-text">{error}</pre>
          </div>
        )}

        {/* Submission ID */}
        {submissionId && (
          <div className="section">
            <h2>3. Submission ID</h2>
            <div className="submission-id">
              <code>{submissionId}</code>
              <button onClick={() => navigator.clipboard.writeText(submissionId)}>
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Score Retrieval */}
        {submissionId && (
          <div className="section">
            <h2>4. Get Scores</h2>
            <div className="score-controls">
              <button
                onClick={() => handleGetScores(false)}
                disabled={loading}
                className="score-btn"
              >
                {loading ? 'Loading...' : 'Get Score'}
              </button>
              <button
                onClick={() => handleGetScores(true)}
                disabled={loading}
                className="score-btn"
              >
                {loading ? 'Loading...' : 'Get All Scores'}
              </button>
            </div>
          </div>
        )}

        {/* Scores Display */}
        {scores && (
          <div className="section">
            <h2>5. Scores Result</h2>
            <pre className="scores-display">{JSON.stringify(scores, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleApp;